import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignQuoteModel, CampaignQuoteItemModel, InvoiceModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { EmailParams } from '@hooks/useCampaignQuote'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuote/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuote/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuote/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuote/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuote/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuote/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuote/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuote/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuote/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuote/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuote/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuote/DELETE_FAILURE'

export type CampaignQuoteModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuotes(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuotes.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quotes.json', options)
      .then(({ data }: { data: CampaignQuoteModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuote(campaignQuoteId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuotes.loadedForKeys]
    const loadedIds = [...state.campaignQuotes.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteId && !loadedIds.includes(campaignQuoteId)){
      loadedIds.push(campaignQuoteId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quotes/${campaignQuoteId}.json`, options)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuote(campaignQuote: Partial<CampaignQuoteModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote: campaignQuote,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quotes.json', options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteFromExisting(
  campaignQuote: Partial<CampaignQuoteModel>,
  existingCampaignQuoteId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote: campaignQuote,
      existing_campaign_quote_id: existingCampaignQuoteId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quotes/create_from_existing.json', options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteFromTemplate(
  campaignQuote: Partial<CampaignQuoteModel>,
  campaignQuoteTemplateId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote: campaignQuote,
      campaign_quote_template_id: campaignQuoteTemplateId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quotes/create_from_template.json', options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createInvoiceFromCampaignQuote(
  campaignQuote: UpdateParams<CampaignQuoteModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}/create_invoice.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuote(
  campaignQuote: UpdateParams<CampaignQuoteModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote: campaignQuote,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteKey(
  params: {
    campaignQuoteId: number | string,
    key: string,
    value: string | number,
  },
) {
  const { campaignQuoteId, key, value } = params

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { campaignQuotes } = state.entities

    const campaignQuote = campaignQuotes[campaignQuoteId]

    if (campaignQuote){
      const updatedQuote = { ...campaignQuote, [key]: value }

      const normalizedJson = normalize(updatedQuote, Schemas.CAMPAIGN_QUOTE)
      // @ts-expect-error TS2345
      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else {
      dispatch(updateFailure(['Campaign Quote does not exist.']))
    }
  }
}

export function updateCampaignQuoteSectionItemOrder(
  campaignQuote: CampaignQuoteModel,
  sortedSectionItemIds: {
    [key: string]: number[],
  },
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      sorted_section_items: sortedSectionItemIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}/update_section_item_sort.json`, options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkUpdateCampaignQuoteItemsPrices(
  campaignQuote: CampaignQuoteModel,
  campaign_quote_item_ids: Array<number>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_item_ids,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}/bulk_update_campaign_quote_items_prices.json`, options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCampaignQuote(campaignQuote: DeleteParams<CampaignQuoteModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function sendQuote(campaignQuoteId: number, emailParams: EmailParams, options = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...emailParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaign_quotes/${campaignQuoteId}/send_quote.json`, options, config)
      .then(({ data }: { data: { id: number } }) => {
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type ValidateCampaignQuotePricesResult = {
  data: {
    data: {
      invalid: CampaignQuoteItemModel[],
      valid: CampaignQuoteItemModel[],
    },
  },
}

export function validateCampaignQuotePrices(
  campaignQuoteId: number,
  options: EntityOptions = {},
) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/campaign_quotes/${campaignQuoteId}/validate_prices.json`, options)
      .then(({ data: result }: ValidateCampaignQuotePricesResult) => {
        const normalizedJson = normalize({
          id: campaignQuoteId,
          invalidItems: result.data.invalid,
        }, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: result.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function approveCampaignQuote(
  campaignQuote: CampaignQuoteModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quotes/${campaignQuote.id}/approve.json`, options, config)
      .then(({ data }: { data: CampaignQuoteModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
